import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, WifiOff } from 'lucide-react';
import { User } from '../types';
import { fetchEnquiries, Enquiry, isLifecycleConnected } from '../utils/lifecycleApi';

const SERVICES = ['Wedding', 'Pre-Wedding', 'Engagement', 'Anniversary', 'Birthday', 'Office Event', 'Any Event'];
const STATUS_BADGE: Record<string, string> = { Open: 'badge-blue', Confirmed: 'badge-teal', Lost: 'badge-red', 'On Hold': 'badge-amber' };

export default function MasterEnquiry({ user }: { user: User }) {
  const [connected] = useState(isLifecycleConnected());
  const [allEnquiries, setAllEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterService, setFilterService] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!connected) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const results = await Promise.all(SERVICES.map(s => fetchEnquiries(s).then(r => r).catch(() => [])));
      setAllEnquiries(results.flat());
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [connected]);

  useEffect(() => { load(); }, [load]);

  const filtered = allEnquiries.filter(e => {
    if (filterService !== 'All' && e.service_type !== filterService) return false;
    if (filterStatus !== 'All' && e.overall_status !== filterStatus) return false;
    if (search && !e.customer_name.toLowerCase().includes(search.toLowerCase()) && !e.ref_number.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const countByService = SERVICES.reduce((acc, s) => ({ ...acc, [s]: allEnquiries.filter(e => e.service_type === s).length }), {} as Record<string, number>);
  const countByStatus = ['Open', 'Confirmed', 'Lost', 'On Hold'].reduce((acc, s) => ({ ...acc, [s]: allEnquiries.filter(e => e.overall_status === s).length }), {} as Record<string, number>);

  if (!connected) return (
    <div className="page-content">
      <div className="alert alert-error"><WifiOff size={15} /> <span>Not connected to Lifecycle backend.</span></div>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>Master Enquiry View</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>All enquiries across all services — search, filter, and get the full picture</p>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={load} title="Refresh"><RefreshCw size={15} /></button>
      </div>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}><AlertTriangle size={15} /> <span>{error}</span></div>}

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div className="card" style={{ flex: '0 0 auto', padding: '12px 20px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>TOTAL</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{allEnquiries.length}</div>
        </div>
        {['Open', 'Confirmed', 'On Hold', 'Lost'].map(s => (
          <div key={s} className="card" style={{ flex: '0 0 auto', padding: '12px 20px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.toUpperCase()}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s === 'Confirmed' ? 'var(--teal)' : s === 'Lost' ? '#E53E3E' : 'var(--text-primary)' }}>{countByStatus[s] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input className="input" style={{ width: 220 }} placeholder="Search name or ref#" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ width: 160 }} value={filterService} onChange={e => setFilterService(e.target.value)}>
          <option value="All">All Services</option>
          {SERVICES.map(s => <option key={s}>{s} ({countByService[s] || 0})</option>)}
        </select>
        <select className="input" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="All">All Statuses</option>
          {['Open', 'Confirmed', 'On Hold', 'Lost'].map(s => <option key={s}>{s}</option>)}
        </select>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{filtered.length} result(s)</span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading all services…</div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Ref #', 'Customer', 'Service', 'Event Dates', 'Package', 'Follow-up', 'Status', 'Created'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No enquiries found</td></tr>
              ) : filtered.map(e => (
                <tr key={e.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--gold)', fontSize: 12 }}>{e.ref_number}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontWeight: 500 }}>{e.customer_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{e.contact_number}</div>
                  </td>
                  <td style={{ padding: '10px 14px' }}><span className="badge badge-amber" style={{ fontSize: 10 }}>{e.service_type}</span></td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', fontSize: 12 }}>{e.event_dates || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', fontSize: 12 }}>{e.suggested_package || '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>
                    {e.follow_up_date && <div style={{ color: 'var(--text-secondary)' }}>{e.follow_up_date}</div>}
                    <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{e.follow_up_status}</div>
                  </td>
                  <td style={{ padding: '10px 14px' }}><span className={`badge ${STATUS_BADGE[e.overall_status] || 'badge-amber'}`} style={{ fontSize: 11 }}>{e.overall_status}</span></td>
                  <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-muted)' }}>{new Date(e.created_at).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
